/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from 'react';
import { useAuthState } from "../../utilities/contexts/auth-state-context";
import UserService from '../../utilities/services/user-service';
import { enqueueSnackbar } from 'notistack';
import { ToggleButton } from '../forms';
import { Button } from '../button/button';
import { useTranslation } from 'react-i18next';
import { createLogger } from '../../utilities/logger';

const log = createLogger("NotificationSettings");

const COMPONENT_CLASS = "c-notifications";

type PlatformUI = {
  highRiskUser: boolean;
  assessments: boolean;
  streaksToDos: boolean;
  newAccounts: boolean;
};

type EmailSettings = {
  baselineAssessment: boolean;
  highRiskUser: boolean;
  startedProgram: boolean;
  completedProgram: boolean;
};

interface NotificationRowConfig {
  key: string;
  titleKey: string;
  descKey: string;
  emailField?: keyof EmailSettings;
  platformField?: keyof PlatformUI;
}

const NOTIFICATION_ROWS: NotificationRowConfig[] = [
  { key: 'highRisk', titleKey: 'settings.notifications.highRisk', descKey: 'settings.notifications.highRiskDescription', emailField: 'highRiskUser', platformField: 'highRiskUser' },
  { key: 'assessments', titleKey: 'settings.notifications.assessments', descKey: 'settings.notifications.assessmentsDescription', emailField: 'baselineAssessment', platformField: 'assessments' },
  { key: 'streaksToDos', titleKey: 'settings.notifications.streaks', descKey: 'settings.notifications.streaksDescription', platformField: 'streaksToDos' },
  { key: 'newAccounts', titleKey: 'settings.notifications.newAccounts', descKey: 'settings.notifications.newAccountsDescription', platformField: 'newAccounts' },
  { key: 'startedProgram', titleKey: 'settings.notifications.startedProgram', descKey: 'settings.notifications.startedProgramDescription', emailField: 'startedProgram' },
  { key: 'completedProgram', titleKey: 'settings.notifications.completedProgram', descKey: 'settings.notifications.completedProgramDescription', emailField: 'completedProgram' },
];

const notificationSettingsToUI = (ns: any): PlatformUI => ({
  highRiskUser: ns?.highRiskUser ?? false,
  assessments: ns?.baselineAssessment ?? false,
  streaksToDos: (ns?.dayStreakMilestone && ns?.completedTasks) ?? false,
  newAccounts: (ns?.newAccount && ns?.inactiveUser) ?? false,
});

const uiToNotificationSettings = (ui: PlatformUI) => ({
  inactiveUser: ui.newAccounts,
  highRiskUser: ui.highRiskUser,
  dayStreakMilestone: ui.streaksToDos,
  newAccount: ui.newAccounts,
  completedTasks: ui.streaksToDos,
  baselineAssessment: ui.assessments,
});

const defaultEmailSettings = (): EmailSettings => ({
  baselineAssessment: false,
  highRiskUser: false,
  startedProgram: false,
  completedProgram: false,
});

const Notifications: React.FC = () => {
  const { t } = useTranslation();
  const { state, setState } = useAuthState();

  const [platformUI, setPlatformUI] = useState<PlatformUI>({
    highRiskUser: false,
    assessments: false,
    streaksToDos: false,
    newAccounts: false,
  });

  const [emailSettings, setEmailSettings] = useState<EmailSettings>({
    baselineAssessment: false,
    highRiskUser: false,
    startedProgram: false,
    completedProgram: false,
  });

  const handleToggleEmail = useCallback((field: keyof EmailSettings) => {
    setEmailSettings(prev => ({ ...prev, [field]: !prev[field] }));
  }, []);

  const handleTogglePlatform = useCallback((field: keyof PlatformUI) => {
    setPlatformUI(prev => ({ ...prev, [field]: !prev[field] }));
  }, []);

  useEffect(() => {
    if (!state.user) return;

    const ns = state.user.notificationSettings;
    if (ns) {
      setPlatformUI(notificationSettingsToUI(ns));
    }

    const ens = state.user.emailNotificationSettings;
    if (ens) {
      setEmailSettings(ens);
    } else if (state.user.id) {
      const defaults = defaultEmailSettings();
      setEmailSettings(defaults);

      UserService.get(state.user.id).then((user) => {
        if (user && !user.emailNotificationSettings) {
          user.emailNotificationSettings = defaults;
          UserService.update(user, state.user).then(() => {
            const stateOrg = state.user;
            stateOrg!.emailNotificationSettings = defaults;
            setState((s) => ({ ...s, ...{ user: stateOrg } }));
          });
        }
      });
    }
  }, [state.user]);

  return (
    <div className={COMPONENT_CLASS}>
      <div className={`${COMPONENT_CLASS}__container`}>
        <h2>{t('settings.notifications.title')}</h2>
        <div className={`${COMPONENT_CLASS}__toggleHeaders`}>
          <span className={`${COMPONENT_CLASS}__toggleHeader`}>{t('settings.notifications.emailHeader')}</span>
          <span className={`${COMPONENT_CLASS}__toggleHeader`}>{t('settings.notifications.platformHeader')}</span>
        </div>
        {NOTIFICATION_ROWS.map((row) => (
          <div key={row.key} className={`${COMPONENT_CLASS}__info`}>
            <div className={`${COMPONENT_CLASS}__notificationText`}>
              <p className='body-bold' style={{ marginBottom: '0.5rem' }}>{t(row.titleKey)}</p>
              <div className='body'>{t(row.descKey)}</div>
            </div>
            <div className={`${COMPONENT_CLASS}__toggles`}>
              <ToggleButton
                id={`${row.key}EmailSetting`}
                isChecked={row.emailField ? emailSettings[row.emailField] : false}
                disabled={!row.emailField}
                onClick={row.emailField ? () => handleToggleEmail(row.emailField!) : undefined}
              />
              <ToggleButton
                id={`${row.key}PlatformSetting`}
                isChecked={row.platformField ? platformUI[row.platformField] : false}
                disabled={!row.platformField}
                onClick={row.platformField ? () => handleTogglePlatform(row.platformField!) : undefined}
              />
            </div>
          </div>
        ))}
        <div className={'c-login-modal__button-area button-gap'}>
          <Button
            type='link'
            buttonText={t('buttons.btn_cancel')}
            onClick={() => {
              setPlatformUI(notificationSettingsToUI(state.user?.notificationSettings));
              setEmailSettings(state.user?.emailNotificationSettings ?? defaultEmailSettings());
            }}
          />
          <Button
            buttonStyle='primary'
            buttonText={t('buttons.btn_saveChanges')}
            onClick={async () => {
              if (!state.user?.id) {
                log.warn("Notification settings save blocked: no user");
                return;
              }
              try {
                const user = await UserService.get(state.user.id);
                if (!user) {
                  log.warn("Notification settings save blocked: user not found", {
                    subjectUserId: state.user.id,
                  });
                  return;
                }
                user.notificationSettings = uiToNotificationSettings(platformUI);
                user.emailNotificationSettings = emailSettings;
                await UserService.update(user, state.user);
                const stateOrg = state.user;
                stateOrg!.notificationSettings = user.notificationSettings;
                stateOrg!.emailNotificationSettings = emailSettings;
                setState((s) => ({ ...s, ...{ user: stateOrg } }));
                log.info("Notification settings saved", {
                  subjectUserId: state.user.id,
                });
                enqueueSnackbar(t('settings.infoUpdated'), { variant: "toast", width: "450px", autoHideDuration: 5000 });
              } catch (e) {
                log.error("Failed to save notification settings", {
                  exception: e,
                  subjectUserId: state.user?.id,
                });
              }
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default Notifications;
